import React, { useEffect, useState } from 'react';
import styled, { x } from '@xstyled/styled-components';
import { Item, registry, RenderProps } from '../Item';
import { GAUGE } from '../itemIds';
import { GaugeProvider, useGaugeStore } from './GaugeStore';
import arrowFlatImage from './arrowForFlat.svg';
import arrowRoundImage from './arrowForRound.svg';
import { useMediaPlayer } from '~/ui/video-player/useMediaPlayer';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useTrainingParams } from '~/training/useTrainingParams';
import { getEditorState } from '~/shell/Store';
// removed tooltip copy import

// Audio feedback now only disabled while in editor (contentEditable) mode; learners still hear feedback.

type StepDef = {
  id?: string;
  color: string;
  value: number;
  label?: string;
  feedback?: string;
  feedbackAudios?: Array<{
    mediaUrl?: string | null;
    subtitlesUrl?: string | null;
  }>;
};

interface Props {
  steps: Array<StepDef>;
  from?: string;
  to?: string;
  shape: 'round' | 'flat';
  feedback?: string;
}

interface FlatViewProps {
  steps: Array<StepDef>;
}

interface RoundViewProps {
  steps: Array<StepDef>;
  from?: string;
  to?: string;
}

const Wrapper = styled.divBox`
  margin-left: auto;
  margin-right: auto;
  margin-top: 30px;
  padding-bottom: 30px;
  width: 100%;
`;

const ArrowRound = styled(arrowRoundImage)`
  margin-left: auto;
  margin-right: auto;
  display: block;
  max-width: 400px;
  /* updated multi-layer purple shadow */

  background: radial-gradient(
    circle,
    rgba(173, 0, 255, 0.3) 0,
    rgba(128, 0, 255, 0.12) 40%,
    rgba(128, 0, 255, 0.04) 0%,
    transparent 7%
  );
`;

const ArrowFlat = styled(arrowFlatImage)`
  margin-top: -30px;
  margin-left: auto;
  margin-right: auto;
  /* updated multi-layer purple shadow */

  background: radial-gradient(
    circle,
    rgba(173, 0, 255, 0.3) 0,
    rgba(128, 0, 255, 0.12) 40%,
    rgba(128, 0, 255, 0.04) 0%,
    transparent 7%
  );
`;

const Round = styled.divBox`
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  aspect-ratio: 2/1;
  position: relative;
  > svg {
    width: 100%;
    aspect-ratio: 2/1;
  }
  > svg > g > text {
    cursor: pointer;
    font-size: 3px;
  }
  > svg > g > circle:focus {
    filter: brightness(0.6);
    outline: 0;
  }
  & .feedback {
    display: block;
    margin-top: 45px;
    text-align: center;
    position: absolute;
    width: 100%;
  }
  & .from {
    font-size: 18px;
    color: black;
    float: left;
    padding-top: 15px;
  }
  & .to {
    font-size: 18px;
    color: black;
    float: right;
    padding-top: 15px;
  }
  & .arrow {
    position: absolute;
    left: calc(50% - 52px);
    transform-origin: 52px 180px;

    top: 0px;
    > svg {
      height: 30% !important;
    }
  }
  @media only screen and (max-width: 768px) {
    & .arrow {
      top: -60px;
    }
  }
`;

const Flat = styled.divBox`
  width: 420px;
  height: 87px;
  border-radius: 16px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: stretch;
  align-content: space-between;
  margin-left: auto;
  margin-right: auto;
  > button {
    width: 100%;
    text-align: center;
    font-size: 40px;
    line-height: 87px;
    cursor: pointer;
    font-family: Recoleta, var(--title-font), 'Recoleta', serif;
  }

  > button:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }
  > button:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
  @media (max-width: sm) {
    max-width: 300px;
  }
`;

// Tooltip removed entirely for this template

function getOperatingSystem() {
  let operatingSystem = 'Not known';
  if (window.navigator.appVersion.indexOf('Win') !== -1) {
    operatingSystem = 'Windows';
  }
  if (window.navigator.appVersion.indexOf('Mac') !== -1) {
    operatingSystem = 'Mac';
  }
  if (window.navigator.appVersion.indexOf('X11') !== -1) {
    operatingSystem = 'UNIX';
  }
  if (window.navigator.appVersion.indexOf('Linux') !== -1) {
    operatingSystem = 'Linux';
  }
  return operatingSystem;
}

const OS = getOperatingSystem();

// Helper to force drag image to just the pointer SVG (exclude surrounding button/label)
function setPointerDragImage(e: React.DragEvent, center = true) {
  try {
    const svg = (e.currentTarget as HTMLElement).querySelector('svg');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGElement;
    clone.style.position = 'absolute';
    clone.style.top = '-1000px';
    clone.style.left = '-1000px';
    clone.style.pointerEvents = 'none';
    if (document.body) {
      document.body.appendChild(clone);
    }
    let offsetX = 0;
    let offsetY = 0;
    if (center) {
      const rect = clone.getBoundingClientRect();
      offsetX = rect.width / 2;
      offsetY = rect.height / 2;
    }
    e.dataTransfer.setDragImage(clone, offsetX, offsetY);
    // Cleanup after this tick
    setTimeout(() => {
      if (clone.parentNode) clone.parentNode.removeChild(clone);
    }, 0);
  } catch (_) {
    // ignore
  }
}

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset');

const PlayAudioFeedback = ({
  narration,
  subtitlesUrl
}: {
  narration?: string;
  subtitlesUrl?: string;
}) => {
  useMediaPlayer(narration, 'audio', subtitlesUrl, true);
  return null;
};

const selector = (s: TrainingState) =>
  select(s, 'saveQuestionAnswers', 'status');

const FlatView = ({ steps }: FlatViewProps) => {
  const {
    select,
    activeItem,
    feedback,
    mediaUrl,
    setMediaUrl,
    subtitlesUrl,
    setSubtitlesUrl
  } = useGaugeStore();
  const { reset } = useMediaPlayerStore(mediaSelector);
  const { saveQuestionAnswers } = useTrainingStore(selector);
  const [defaultId, setDefaultId] = useState('');

  const { sequenceId } = useTrainingParams();
  const contentEditable = getEditorState().contentEditable;
  // tooltip removed
  const [dragging, setDragging] = useState(false);
  const isEditor = contentEditable;
  useEffect(() => {
    if (activeItem === null && steps?.length) {
      const defaultIndex = Math.floor(steps.length / 2);
      const defaultStep = steps[defaultIndex];

      if (!defaultStep) return;
      if (defaultStep.id) setDefaultId(defaultStep.id as string);
      select(defaultStep.value);
    }
  }, []);

  useEffect(() => {
    if (defaultId) {
      saveQuestionAnswers(defaultId, sequenceId);
    }
  }, [defaultId]);

  const onClick = (item: StepDef, index: number) => {
    const audio = item?.feedbackAudios?.[0];

    if (item.id) {
      saveQuestionAnswers(item.id, sequenceId);
    }

    if (!contentEditable && audio?.mediaUrl) {
      reset();
      setMediaUrl(`${audio.mediaUrl}?i=${index}`);
      setSubtitlesUrl(audio.subtitlesUrl ?? '');
    }
    select(item?.value, item?.feedback);
    // tooltip removed
  };

  return (
    <>
      <Flat className="flatGauge brightnessStyleForDarkTheme">
        {steps.map((item: StepDef, index: number) => {
          const middleIndex = Math.floor(steps.length / 2);
          const showDefaultArrow =
            (activeItem === null || activeItem === undefined) &&
            index === middleIndex;
          return (
            <x.div
              className="flatGaugeButton"
              tabIndex={0} // always focusable for accessibility
              as="button"
              backgroundColor={item.color}
              position="relative"
              // Allow BOTH click and drag in learner mode now
              {...(!isEditor
                ? {
                    onClick: () => onClick(item, index),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick(item, index);
                      }
                    },
                    onDragOver: (e: React.DragEvent) => e.preventDefault(),
                    onDrop: (e: React.DragEvent) => {
                      e.preventDefault();
                      onClick(item, index);
                    }
                  }
                : {
                    onClick: () => onClick(item, index),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick(item, index);
                      }
                    }
                  })}
              key={`FlatViewItem${index}`}
              style={{ cursor: 'pointer' }}
              aria-pressed={activeItem === item.value}
            >
              {item.label}
              {!isEditor && (
                <x.div
                  aria-hidden="true"
                  position="absolute"
                  left={0}
                  bottom={-32}
                  zIndex={1}
                  onDragOver={(e: React.DragEvent) => e.preventDefault()}
                  onDrop={(e: React.DragEvent) => {
                    e.preventDefault();
                    onClick(item, index);
                  }}
                  style={{
                    pointerEvents: 'auto',
                    width: '100%',
                    height: '32px'
                  }}
                />
              )}
              {(activeItem == item.value || showDefaultArrow) && (
                <x.div
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mt="-10px"
                  overflow="visible"
                  position="relative"
                >
                  <x.div
                    draggable={!isEditor}
                    onDragStart={e => {
                      if (isEditor) return;
                      e.dataTransfer.effectAllowed = 'move';
                      setDragging(true);
                      setPointerDragImage(e);
                    }}
                    onDragEnd={() => setDragging(false)}
                    onMouseDown={() => {
                      if (!isEditor) {
                        setDragging(true);
                      }
                    }}
                    onMouseUp={() => setDragging(false)}
                    cursor={
                      !isEditor ? (dragging ? 'grabbing' : 'grab') : undefined
                    }
                    style={
                      !isEditor
                        ? { cursor: dragging ? 'grabbing' : 'grab' }
                        : undefined
                    }
                  >
                    <ArrowFlat />
                  </x.div>
                </x.div>
              )}
            </x.div>
          );
        })}
      </Flat>
      <x.div textAlign="center" marginTop="25px">
        <b dangerouslySetInnerHTML={{ __html: feedback || '' }} />
        {!contentEditable && mediaUrl && (
          <PlayAudioFeedback narration={mediaUrl} subtitlesUrl={subtitlesUrl} />
        )}
      </x.div>
    </>
  );
};

const RoundView = ({ steps, from, to }: RoundViewProps) => {
  const {
    select,
    activeItem,
    arrowRotate,
    setArrowRotate,
    feedback,
    mediaUrl,
    setMediaUrl,
    subtitlesUrl,
    setSubtitlesUrl
  } = useGaugeStore();
  const percent = 100 / 2 / steps.length;
  const stepForRotation = 180 / steps.length;
  const fallbackIndex = Math.floor(steps.length / 2);
  const fallbackRotate =
    90 + stepForRotation / 2 - stepForRotation * (fallbackIndex + 1);
  const arrowTransform = `rotate(${
    activeItem === null ? fallbackRotate : arrowRotate
  }deg)`;
  const { reset } = useMediaPlayerStore(mediaSelector);
  const { saveQuestionAnswers } = useTrainingStore(selector);
  const [defaultId, setDefaultId] = useState('');
  const { sequenceId } = useTrainingParams();
  const contentEditable = getEditorState().contentEditable;
  // tooltip removed
  const [dragging, setDragging] = useState(false);
  const isEditor = contentEditable;

  // Initialize default selection & arrow AFTER first render
  useEffect(() => {
    if (activeItem === null && steps?.length) {
      const defaultIndex = Math.floor(steps.length / 2);
      const defaultStep = steps[defaultIndex];
      if (!defaultStep) return;
      if (defaultStep.id) setDefaultId(defaultStep.id as string);
      select(defaultStep.value);
      const x = stepForRotation * (defaultIndex + 1);
      setArrowRotate(90 + stepForRotation / 2 - x);
    }
  }, [activeItem, steps, stepForRotation]);

  useEffect(() => {
    if (defaultId) {
      saveQuestionAnswers(defaultId, sequenceId);
    }
  }, [defaultId]);

  const circles = steps
    .slice(0)
    .reverse()
    .map((el: StepDef, index: number) => {
      const percentage = 100 - index * percent;
      const x = (percentage * 31.4) / 100;
      const calcRadius = `${x} 31.4`;
      const audio = el?.feedbackAudios && el.feedbackAudios[0];

      const firstEmoji = {
        x: OS == 'Windows' ? 15.6 : 16.2,
        y: 9
      };

      const lastEmoji = {
        x: OS == 'Windows' ? 0.2 : 0.7,
        y: 9
      };

      const onSelectItem = () => {
        if (el.id) {
          saveQuestionAnswers(el.id, sequenceId);
        }

        if (!contentEditable && audio?.mediaUrl) {
          reset();
          setMediaUrl(`${audio.mediaUrl}?i=${index}`);
          setSubtitlesUrl(
            audio.subtitlesUrl ? `${audio.subtitlesUrl}?vtt=${index}` : ''
          );
        }

        select(el.value, el.feedback);
        // tooltip removed

        if (index == 0) {
          setArrowRotate(90 - stepForRotation / 2);
        } else if (index == steps.length - 1) {
          setArrowRotate(-90 + stepForRotation / 2);
        } else {
          const x = stepForRotation * (index + 1);
          setArrowRotate(90 + stepForRotation / 2 - x);
        }
      };

      const eventHandlers = !isEditor
        ? {
            onClick: onSelectItem, // NEW: learner click support
            onDragOver: (e: React.DragEvent) => e.preventDefault(),
            onDrop: (e: React.DragEvent) => {
              e.preventDefault();
              onSelectItem();
            }
          }
        : { onClick: onSelectItem };
      return (
        <g
          key={`g-${el.color}${index}`}
          {...eventHandlers}
          style={{ cursor: 'pointer' }}
        >
          <circle
            cursor="pointer"
            r="5"
            cx="10"
            cy="10"
            key={`${el.color}${index}`}
            fill="transparent"
            stroke={el.color}
            strokeWidth="10"
            strokeDasharray={calcRadius}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key == 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectItem();
              }
            }}
          />
          {index == 0 && (
            <text x={firstEmoji.x} y={firstEmoji.y} fill="blue">
              {el.label}
            </text>
          )}
          {index == steps.length - 1 && (
            <text x={lastEmoji.x} y={lastEmoji.y} fill="blue">
              {el.label}
            </text>
          )}
        </g>
      );
    });

  return (
    <Round>
      <svg
        key="roundViewChart"
        viewBox="0 0 20 10"
        fill="white"
        className="brightnessStyleForDarkTheme"
      >
        {circles}
        <circle key="whiteCircle" r="6" cx="10" cy="10" fill="white" />
      </svg>
      <x.div
        key="blockWithArrow"
        className="arrow"
        style={{
          transform: arrowTransform
        }}
      >
        <x.div
          display="flex"
          flexDirection="column"
          overflow="visible"
          alignItems="center"
          position="relative"
        >
          <x.div
            draggable={!isEditor}
            onDragStart={e => {
              if (isEditor) return;
              e.dataTransfer.effectAllowed = 'move';
              // tooltip removed
              setDragging(true);
              setPointerDragImage(e);
            }}
            onDragEnd={() => setDragging(false)}
            onMouseDown={() => {
              if (!isEditor) {
                // tooltip removed
                setDragging(true);
              }
            }}
            onMouseUp={() => setDragging(false)}
            cursor={!isEditor ? (dragging ? 'grabbing' : 'grab') : undefined}
            style={
              !isEditor ? { cursor: dragging ? 'grabbing' : 'grab' } : undefined
            }
          >
            <ArrowRound key="arrowRoundSvg" />
          </x.div>
          {/* tooltip removed */}
        </x.div>
      </x.div>
      <div className="footerForGauge">
        <x.div
          key="RoundViewFrom"
          className="from"
          dangerouslySetInnerHTML={{ __html: from as string }}
        />
        <x.div
          key="RoundViewTo"
          className="to"
          dangerouslySetInnerHTML={{ __html: to as string }}
        />
        <x.div className="feedback">
          <b dangerouslySetInnerHTML={{ __html: feedback || '' }} />
          {!contentEditable && mediaUrl && (
            <PlayAudioFeedback
              narration={mediaUrl}
              subtitlesUrl={subtitlesUrl}
            />
          )}
        </x.div>
      </div>
    </Round>
  );
};

export class Gauge extends Item<typeof GAUGE, Props> {
  protected render({ steps, from, to, shape }: RenderProps<Props>) {
    return (
      <GaugeProvider>
        <Wrapper>
          {shape == 'flat' ? (
            <FlatView steps={steps} />
          ) : (
            <RoundView steps={steps} from={from} to={to} />
          )}
        </Wrapper>
      </GaugeProvider>
    );
  }
}

registry[GAUGE] = Gauge;
